import { Button, Col, Form, Modal, Row } from 'antd';
import dayjs from 'dayjs';
import React, { useContext, useEffect, useState } from 'react';

import ButtonComponent from 'components/atoms/ButtonComponent';
import CheckboxComponent from 'components/atoms/CheckboxComponent';
import DatePickerComponent from 'components/atoms/DatePickerComponent';
import InputComponent from 'components/atoms/InputComponent';
import Paragraph from 'components/atoms/Paragraph';
import SelectComponent from 'components/atoms/SelectComponent';
import Title from 'components/atoms/Title';
import LoadingWrapper from 'components/hoc/LoadingWrapper';
import { Context } from 'components/hoc/Store';
import TranslateText from 'components/hoc/TranslateText';
import PrilohyTable from 'components/molecules/Dokumenty/PrilohyTable';
import FormWrapper from 'components/molecules/FormWrapper';

import useProfily from 'hooks/useProfily';

import { IDokZapisPpn, dokZapisPpnGet, dokZapisPpnPut } from 'services/dokumenty/prikazy';
import { ISmlouvniStrany, dokumentIsinApi, dokumentOsobyApi } from 'services/dokumenty/smlouva';

import { EEvidenceCp } from 'enums/isin/evidenceCp';
import { EDruhCp } from 'enums/isin/udaje/druhCp';

import dltUcetValidation from 'utils/validations/dltUcetValidation';

import Osoby from '../Osoby';
import Podpisy from '../Podpisy';
import { getDataIsins } from './Zruseni';

interface IIZapisPpnFirstStep {
    nextStep: () => void;
    zadostID: number;
    prikazyData: ISmlouvniStrany;
    setCurrentStep: React.Dispatch<React.SetStateAction<number>>;
    setLoading: React.Dispatch<React.SetStateAction<boolean>>;
    setData: React.Dispatch<React.SetStateAction<IDokZapisPpn | undefined>>;
    data?: IDokZapisPpn;
    signingDisabled: boolean;
}

const IZapisPpnFirstStep: React.FC<IIZapisPpnFirstStep> = ({
    nextStep,
    zadostID,
    prikazyData,
    setCurrentStep,
    setData,
    data,
    setLoading,
    signingDisabled
}) => {
    const { setLevel } = useProfily();
    const { dokumenty } = useContext(Context);
    const [form] = Form.useForm();
    const [dataIsins] = useState<
        {
            labelcz: string;
            value: string;
        }[]
    >(
        dokumenty?.dokumentyPrava
            ? getDataIsins(dokumenty)
                  .filter(e => {
                      const parsedData = e.data ? JSON.parse(e.data) : undefined;
                      return (
                          (!parsedData || parsedData.evidencecp === EEvidenceCp.CDCPDLT) && e.druhcp === EDruhCp.AKCIE
                      );
                  })
                  .map(e => {
                      return {
                          labelcz: e.isin,
                          value: e.isin
                      };
                  })
            : []
    );
    const [selectedIsin, setSelectIsin] = useState<any>();

    useEffect(() => {
        if (!selectedIsin && data?.isin) {
            setSelectIsin(JSON.parse(getDataIsins(dokumenty).filter(e => e.isin === data.isin)[0].data));
            form.setFieldsValue({
                ...data,
                datum: data.datum ? dayjs(data.datum) : undefined
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);

    useEffect(() => {
        if (!dataIsins.length) {
            dokumentIsinApi().then(resp => {
                setLevel('dokumenty', {
                    ...dokumenty,
                    dokumentyPrava: resp,
                    dokumentyMenuReady: true
                });
            });
            setCurrentStep(3);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <>
            {data && prikazyData && (
                <>
                    <FormWrapper
                        outForm={form}
                        onFinish={values => {
                            setLoading(true);

                            const dataToSend = {
                                ...(values as any),
                                datum: values.datum.format('YYYY-MM-DD'),
                                ...(data.souhlas && {
                                    souhlas: true
                                })
                            };
                            delete dataToSend.idmbSpolecnost;
                            delete dataToSend.dicdphSpolecnost;
                            delete dataToSend.nazevSpolecnost;
                            delete dataToSend.leiSpolecnost;
                            setData(dataToSend);
                            dokZapisPpnPut(zadostID, dataToSend)
                                .then(() => nextStep())
                                .finally(() => setLoading(false));
                        }}
                    >
                        <Title text="EMITENT" level={5} />
                        <InputComponent
                            disabled={true}
                            initialValue={prikazyData?.spolecnost.idmb}
                            label="IDENTIFIKACNI_CISLO"
                            name="idmbSpolecnost"
                            notRequired={true}
                        />
                        {prikazyData?.spolecnost.dicdph && (
                            <InputComponent
                                disabled={true}
                                initialValue={prikazyData?.spolecnost.dicdph}
                                label="ISIN_IDENTIFIKACE_DIC"
                                name="dicdphSpolecnost"
                                notRequired={true}
                            />
                        )}
                        <InputComponent
                            disabled={true}
                            initialValue={prikazyData?.spolecnost.nazev}
                            label="ISIN_IDENTIFIKACE_FIRMA"
                            name="nazevSpolecnost"
                            notRequired={true}
                        />
                        <InputComponent
                            disabled={true}
                            initialValue={prikazyData?.spolecnost.lei}
                            label="LEI_CELE"
                            name="leiSpolecnost"
                            notRequired={true}
                        />
                        <SelectComponent
                            name="isin"
                            labelParagraph={'PRIKAZ_ZNIZENI_ISIN'}
                            defaultValue={data?.isin}
                            data={dataIsins}
                            isFormItem={true}
                            disabled={signingDisabled}
                            onChange={val => {
                                setSelectIsin(JSON.parse(getDataIsins(dokumenty).filter(e => e.isin === val)[0].data));
                                setLevel('dokumenty', {
                                    ...dokumenty,
                                    selectedZadost: {
                                        ...dokumenty?.selectedZadost!,
                                        isin: val
                                    }
                                });
                            }}
                        />
                        <DatePickerComponent
                            name="datum"
                            initialValue={data?.datum ? dayjs(data.datum) : undefined}
                            // disabledDate={current => current < dayjs().add(3, 'days')}
                            label="PRIKAZ_PREVOD_DATUM"
                            disabled={signingDisabled}
                        />
                        <InputComponent
                            name="alinearid"
                            label="PRIKAZ_PREVODNASL_MAJETKOV_UCET"
                            initialValue={data?.alinearid}
                            rules={dltUcetValidation()}
                            disabled={signingDisabled}
                        />
                        <PrilohyTable
                            zadostID={zadostID}
                            disabled={signingDisabled}
                            titleTooltip="PRIKAZ_PREVODNASL_MAJETKOV_PRILOHY_TOOLTIP"
                        />

                        <Row className="button-row">
                            <Col>
                                <ButtonComponent buttontext="POKRACOVAT" id="isinPokracovatBtn" htmlType="submit" />
                            </Col>
                        </Row>
                    </FormWrapper>
                </>
            )}
        </>
    );
};

interface IIZapisPpnPodminky {
    nextStep: () => void;
    prevStep: () => void;
    signingDisabled: boolean;
    data: IDokZapisPpn;
    setData: React.Dispatch<React.SetStateAction<IDokZapisPpn | undefined>>;
    zadostID: number;
    setLoading: React.Dispatch<React.SetStateAction<boolean>>;
}

const IZapisPpnPodminky: React.FC<IIZapisPpnPodminky> = ({
    nextStep,
    prevStep,
    signingDisabled,
    data,
    setData,
    zadostID,
    setLoading
}) => {
    const [isChecked, setIsChecked] = useState<boolean>(signingDisabled);
    const [form] = Form.useForm();

    return (
        <FormWrapper outForm={form} onFinish={() => {}}>
            <Paragraph
                text="PRIKAZ_PREVODNASL_MAJETKOV_PODMINKY"
                style={{
                    display: 'block',
                    marginTop: 16
                }}
            />
            <Row
                style={{
                    marginBottom: 4,
                    marginTop: 26
                }}
            >
                <CheckboxComponent
                    name="souhlas"
                    checkboxText={<TranslateText>POTVRZENI_CHECKBOX</TranslateText>}
                    onChange={() => setIsChecked(pv => !pv)}
                    disabled={signingDisabled}
                    checked={isChecked}
                    defaultChecked={isChecked}
                />
            </Row>
            <Row className="button-row">
                <ButtonComponent buttontext="ZPET" type="default" onClick={() => prevStep()} id="isinZpetBtn" />
                <ButtonComponent
                    buttontext="POKRACOVAT"
                    onClick={() => {
                        nextStep();

                        if (!data.souhlas) {
                            dokZapisPpnPut(zadostID, {
                                ...data,
                                souhlas: true
                            })
                                .then(() => {
                                    nextStep();
                                    setData({
                                        ...data,
                                        souhlas: true
                                    });
                                })
                                .finally(() => setLoading(false));
                        }
                    }}
                    id="isinPokracovatBtn"
                    disabled={!isChecked}
                />
            </Row>
        </FormWrapper>
    );
};

interface IZapisPpn {
    zadostID: number;
    prikazyData: ISmlouvniStrany;
}

const ZapisPpn: React.FC<IZapisPpn> = ({ zadostID, prikazyData }) => {
    const { confirm } = Modal;
    const [currentStep, setCurrentStep] = useState<number>(0);
    const [data, setData] = useState<IDokZapisPpn>();
    const [loading, setLoading] = useState<boolean>(false);
    const signingDisabled = !!data?.souhlas;

    useEffect(() => {
        setLoading(true);
        dokZapisPpnGet(zadostID)
            .then(resp => {
                dokumentOsobyApi(zadostID, true)
                    .then(response => response.osoby.some(e => !!e.podepsano) && setCurrentStep(3))
                    .finally(() => setLoading(false));
                setData(resp);
            })
            .catch(() => {
                setData({} as IDokZapisPpn);
                setLoading(false);
            });

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [zadostID]);

    const next = () => {
        window.scrollTo(0, 0);
        setCurrentStep(currentStep + 1);
    };

    const prev = () => {
        window.scrollTo(0, 0);
        setCurrentStep(currentStep - 1);
    };

    const steps = [
        {
            title: '',
            content: (
                <IZapisPpnFirstStep
                    nextStep={next}
                    zadostID={zadostID!}
                    prikazyData={prikazyData}
                    setCurrentStep={setCurrentStep}
                    data={data}
                    setData={setData}
                    setLoading={setLoading}
                    signingDisabled={signingDisabled}
                />
            )
        },
        {
            title: 'DOKUMENT_DODATEK_SHRNUTI_TITLE',
            content: (
                <IZapisPpnPodminky
                    nextStep={next}
                    prevStep={prev}
                    signingDisabled={signingDisabled}
                    data={data!}
                    setData={setData}
                    setLoading={setLoading}
                    zadostID={zadostID}
                />
            )
        },
        {
            title: '',
            content: <Osoby nextStep={next} prevStep={prev} zadostID={zadostID!} isDodatek={false} />
        },
        {
            title: '',
            content: <Podpisy prevStep={prev} zadostID={zadostID!} showPreControl={false} isPrikaz={true} />
        }
    ];
    return (
        <LoadingWrapper loading={loading}>
            {steps.length - 3 > currentStep && data?.souhlas && (
                <Button
                    style={{
                        top: '30%',
                        right: 10,
                        position: 'fixed',
                        zIndex: 999
                    }}
                    type="primary"
                    onClick={() =>
                        confirm({
                            title: <TranslateText>DOKUMENTY_OSOBY_ZMENIT</TranslateText>,
                            cancelText: <TranslateText>NE</TranslateText>,
                            okText: <TranslateText>ANO</TranslateText>,
                            onOk() {
                                setLoading(true);
                                dokZapisPpnPut(zadostID!, {
                                    ...data!,
                                    souhlas: false
                                })
                                    .then(() => {
                                        setData({
                                            ...data!,
                                            souhlas: false
                                        });
                                    })
                                    .finally(() => setLoading(false));
                            }
                        })
                    }
                >
                    <TranslateText>POVOLIT_EDITACI</TranslateText>
                </Button>
            )}
            <Row gutter={8}>
                <Col span={24}>{steps[currentStep].content}</Col>
            </Row>
        </LoadingWrapper>
    );
};

export default ZapisPpn;
